<template>
  <div class="forum_level_page">
    <section
      class="forum_level_group_header"
      :style="{
        backgroundImage: headerBackground
          ? `url(${headerBackground})`
          : 'linear-gradient(135deg,#ff8c42,#ffb37a)',
      }"
    >
      <div class="forum_level_group_mask" />
      <div class="forum_level_group_content">
        <img v-if="group && group.groupIcon" class="forum_level_group_icon" :src="group.groupIcon" alt="" />
        <div class="forum_level_group_info">
          <h1>{{ group && group.groupName ? group.groupName : '论坛' }}</h1>
          <span v-if="currentLevel" class="forum_level_group_badge">Lv.{{ currentLevel.level }}</span>
        </div>
      </div>
    </section>
    <van-tabs v-model="activeTab" sticky color="rgb(255, 18, 0)" title-active-color="rgb(255, 18, 0)" @change="onTabChange">
      <van-tab title="论坛等级" :name="0">
        <div v-if="levelLoading && !levelData" class="forum_level_state">加载中...</div>
        <div v-else-if="levelError && !levelData" class="forum_level_state"><p>等级信息加载失败</p><button type="button" @click="fetchLevel">重新加载</button></div>
        <template v-else-if="levelData">
          <section class="forum_level_user_card">
            <div class="forum_level_avatar_wrap">
              <img class="forum_level_avatar" :src="levelData.avatar || FORUM_DEFAULT_AVATAR" alt="" />
              <img v-if="levelData.userFrameInfoDTO && levelData.userFrameInfoDTO.frameUrl" class="forum_level_frame" :src="levelData.userFrameInfoDTO.frameUrl" alt="" />
            </div>
            <div class="forum_level_user_info">
              <div class="forum_level_identity">
                <strong>{{ levelData.forumAccount || '暂无论坛账号' }}</strong>
                <span>Lv.{{ currentLevel ? currentLevel.level : 0 }}</span>
              </div>
              <p>当前经验值:{{ currentExperience }}/{{ experienceTarget }}</p>
              <van-progress class="forum_level_progress" :percentage="levelProgress" :show-pivot="false" :stroke-width="8" color="linear-gradient(0deg, #fcc453 0%, #fee38d 100%)" track-color="#ede1d0" />
            </div>
          </section>
          <section v-if="levelData.currentLevelTasks.length" class="forum_level_task_section">
            <h3><span />等级任务</h3>
            <div v-for="task in levelData.currentLevelTasks" :key="task.conditionType" class="forum_level_task">
              <div class="forum_level_task_text"><span>{{ task.conditionName }} ≥ {{ task.targetValue }}</span><em>({{ task.currentValue }}/{{ task.targetValue }})</em></div>
            </div>
          </section>
          <div v-if="levelData.levelRuleContent" class="forum_level_rule" v-html="levelData.levelRuleContent" />
        </template>
      </van-tab>
      <van-tab title="论坛礼包" :name="1">
        <van-list v-model="giftLoading" :finished="giftFinished" :finished-text="gifts.length ? '没有更多了' : ''" @load="fetchGifts(false)">
          <article v-for="gift in gifts" :key="gift.configId" class="forum_gift_item">
            <img :src="gift.giftIcon" alt="" />
            <div class="forum_gift_info"><h3>{{ gift.giftName }}</h3><p>{{ gift.giftContent }}</p><small>{{ gift.conditionDisplay }}</small></div>
            <div class="forum_gift_action">
              <van-button class="forum_gift_receive_btn" size="small" round :color="gift.received ? '#919191' : 'rgb(255, 18, 0)'" :disabled="gift.received" :loading="Boolean(giftReceivingIds[String(gift.configId)])" @click="onReceiveGift(gift)">{{ gift.received ? '已领取' : '领取' }}</van-button>
              <span class="forum_gift_refresh">{{ giftRefreshFrequencyText[gift.refreshFrequency] }}</span>
            </div>
          </article>
        </van-list>
        <div v-if="giftError" class="forum_level_state"><p>礼包加载失败</p><button type="button" @click="refreshGifts">重新加载</button></div>
        <div v-if="!giftLoading && !giftError && gifts.length === 0 && giftFinished" class="forum_level_state">暂无论坛礼包</div>
      </van-tab>
    </van-tabs>
    <ActivityPopup
      :visible.sync="giftPopup.visible"
      :title="giftPopup.title"
      :message="giftPopup.messages"
      :showCancelButton="giftPopup.showCancelButton"
      :cancelButtonText="giftPopup.cancelText"
      :confirmButtonText="giftPopup.confirmText"
      @confirm="onGiftPopupConfirm"
      @cancel="closeGiftPopup"
    />
  </div>
</template>

<script>
import { Button, Icon, List, Progress, Tab, Tabs, Toast } from 'vant';
import { getGroupGiftList, getMyLevelInfo, receiveGroupGift } from '@/api/forum';
import ActivityPopup from '@/components/activityComps/Popup.vue';
import { FORUM_DEFAULT_AVATAR } from './constants';
import trackingMixin, { EVENT_TYPE_ENUM } from './mixins/tracking';

const GIFT_PAGE_SIZE = 10;
const GIFT_REFRESH_FREQUENCY_TEXT = {
  1: '每日刷新',
  2: '每周刷新',
  3: '仅一次',
};

export default {
  name: 'ForumLevel',
  mixins: [trackingMixin],
  components: {
    ActivityPopup,
    [Button.name]: Button,
    [Icon.name]: Icon,
    [List.name]: List,
    [Progress.name]: Progress,
    [Tab.name]: Tab,
    [Tabs.name]: Tabs,
  },
  data() {
    return {
      FORUM_DEFAULT_AVATAR,
      activeTab: 0,
      levelData: null,
      levelLoading: false,
      levelError: false,
      lastLevelFetchedAt: 0,
      initialTabTracked: false,
      gifts: [],
      giftPage: 0,
      giftTotal: 0,
      giftLoading: false,
      giftFinished: false,
      giftError: false,
      giftInitialized: false,
      giftRequesting: false,
      giftReceivingIds: {},
      giftRefreshFrequencyText: GIFT_REFRESH_FREQUENCY_TEXT,
      giftPopupTracking: { giftId: '', giftName: '' },
      giftPopup: {
        visible: false,
        title: '',
        messages: [],
        showCancelButton: false,
        cancelText: '取消',
        confirmText: '确定',
        action: 'close',
      },
    };
  },
  computed: {
    groupId() { return String(this.$route.params.groupId || ''); },
    headerBackground() {
      const forumInfo = this.$store.state.forum.forumInfo;
      if (String(forumInfo.groupId || '') !== this.groupId) return '';
      return forumInfo.background || '';
    },
    currentLevel() { return this.levelData ? this.levelData.currentLevelDTO : null; },
    nextLevel() { return this.levelData ? this.levelData.nextLevelDTO : null; },
    group() { return this.levelData ? this.levelData.groupVo : null; },
    currentExperience() { return Number(this.levelData ? this.levelData.currentExperience : 0); },
    experienceTarget() {
      if (this.nextLevel) return Number(this.nextLevel.experience);
      return Math.max(this.currentExperience, Number(this.currentLevel ? this.currentLevel.experience : 0));
    },
    levelProgress() {
      if (!this.nextLevel) return 100;
      if (this.experienceTarget <= 0) return 0;
      return Math.max(0, Math.min(100, Math.round((this.currentExperience / this.experienceTarget) * 100)));
    },
  },
  created() {
    this.fetchLevel();
    if (this.activeTab === 1 && !this.giftInitialized) this.fetchGifts(false);
  },
  methods: {
    async fetchLevel() {
      if (!this.groupId || this.levelLoading) return;
      const now = Date.now();
      if (this.levelData && now - this.lastLevelFetchedAt < 1000) return;
      this.levelLoading = true;
      this.levelError = false;
      try {
        const res = await getMyLevelInfo(this.groupId);
        this.levelData = res.data;
        this.lastLevelFetchedAt = Date.now();
        if (!this.initialTabTracked) this.trackLevelTab(this.activeTab);
      } catch (error) {
        this.levelError = true;
      } finally {
        this.levelLoading = false;
      }
    },
    mergeGiftList(items) {
      const map = new Map(this.gifts.map((item) => [String(item.configId), item]));
      items.forEach((item) => map.set(String(item.configId), item));
      this.gifts = Array.from(map.values());
    },
    async fetchGifts(reset = false) {
      if (!this.groupId || this.giftRequesting) return;
      this.giftInitialized = true;
      if (reset) {
        this.gifts = [];
        this.giftPage = 0;
        this.giftTotal = 0;
        this.giftFinished = false;
      }
      if (this.giftFinished) {
        this.giftLoading = false;
        return;
      }
      this.giftRequesting = true;
      this.giftLoading = true;
      this.giftError = false;
      try {
        const res = await getGroupGiftList({ groupId: this.groupId, page: this.giftPage + 1, size: GIFT_PAGE_SIZE });
        const data = res.data;
        this.mergeGiftList(Array.isArray(data.list) ? data.list : []);
        this.giftPage = Number(data.currentPage);
        this.giftTotal = Number(data.total);
        this.giftFinished = this.gifts.length >= this.giftTotal;
      } catch (error) {
        this.giftError = true;
      } finally {
        this.giftRequesting = false;
        this.giftLoading = false;
      }
    },
    refreshGifts() { return this.fetchGifts(true); },
    onTabChange(name) {
      this.trackLevelTab(name);
      if (Number(name) === 1 && !this.giftInitialized) this.fetchGifts(false);
    },
    getLevelTrackingFields() {
      return {
        page_type: '我的等级',
        group_id: this.groupId,
        group_name: this.group ? this.group.groupName : '',
      };
    },
    trackLevelTab(name) {
      if (!this.group) return;
      this.EVENT_TRACKING(EVENT_TYPE_ENUM.platform_group_my_level_tab_click, {
        ...this.getLevelTrackingFields(),
        tab_name: Number(name) === 1 ? '论坛礼包' : '论坛等级',
      });
      this.initialTabTracked = true;
    },
    getGiftTrackingFields(gift) {
      return {
        ...this.getLevelTrackingFields(),
        gift_id: gift.configId,
        gift_name: gift.giftName,
        draw_condit: gift.conditionDisplay,
      };
    },
    setGiftReceiving(configId, receiving) {
      const key = String(configId);
      if (receiving) this.$set(this.giftReceivingIds, key, true);
      else this.$delete(this.giftReceivingIds, key);
    },
    markGiftReceived(configId) {
      const item = this.gifts.find((gift) => String(gift.configId) === String(configId));
      if (item) this.$set(item, 'received', true);
    },
    getErrorCode(error) { return String(error && error.code !== undefined ? error.code : ''); },
    getErrorMessage(error, fallback) { return error && error.msg ? error.msg : fallback; },
    openMyGift() { this.$router.push({ name: 'personalCenter.myGift' }); },
    openCustomerService() { this.$router.push({ name: 'service' }); },
    showGiftPopup(config, gift, popType) {
      this.giftPopupTracking = {
        giftId: gift.configId,
        giftName: gift.giftName,
      };
      this.giftPopup = { visible: true, ...config };
      this.EVENT_TRACKING(EVENT_TYPE_ENUM.platform_group_gift_draw_result_pop, {
        ...this.getGiftTrackingFields(gift),
        pop_type: popType,
      });
    },
    closeGiftPopup() {
      this.giftPopup.visible = false;
    },
    onGiftPopupConfirm() {
      const action = this.giftPopup.action;
      this.EVENT_TRACKING(EVENT_TYPE_ENUM.platform_group_gift_draw_result_pop_click, {
        ...this.getLevelTrackingFields(),
        gift_id: this.giftPopupTracking.giftId,
        gift_name: this.giftPopupTracking.giftName,
        button_name: this.giftPopup.confirmText,
      });
      this.closeGiftPopup();
      if (action === 'open-gift') this.openMyGift();
      else if (action === 'contact-service') this.openCustomerService();
    },
    async handleGiftReceiveError(error, gift) {
      const code = this.getErrorCode(error);
      if (code === '7071') {
        this.showGiftPopup({
          title: '不满足领取条件',
          messages: [this.getErrorMessage(error, gift.conditionDisplay)],
          showCancelButton: false,
          cancelText: '取消',
          confirmText: '去完成',
          action: 'close',
        }, gift, '不满足条件');
      } else if (code === '7072') {
        this.markGiftReceived(gift.configId);
        await this.refreshGifts();
      } else if (code === '7073') {
        this.showGiftPopup({
          title: '礼包发放失败',
          messages: ['兑换码生成错误', '请联系客服领取备用码'],
          showCancelButton: false,
          cancelText: '取消',
          confirmText: '联系客服',
          action: 'contact-service',
        }, gift, '礼包码发放失败');
        await this.refreshGifts();
      } else if (code === '7044') {
        this.giftError = true;
      } else {
        Toast(this.getErrorMessage(error, '领取失败，请稍后重试'));
        if (code === '7070' || code === '7074') await this.refreshGifts();
      }
    },
    async onReceiveGift(gift) {
      const key = String(gift.configId);
      if (gift.received || this.giftReceivingIds[key]) return;
      this.EVENT_TRACKING(EVENT_TYPE_ENUM.platform_group_gift_draw_button_click, {
        ...this.getGiftTrackingFields(gift),
      });
      this.setGiftReceiving(gift.configId, true);
      try {
        const res = await receiveGroupGift(this.groupId, gift.configId);
        if (!res.data.received) return;
        this.markGiftReceived(gift.configId);
        this.showGiftPopup({
          title: '领取成功',
          messages: [
            gift.giftName,
            gift.giftContent,
            '礼包码已存放在',
            '【礼包中心】-【我的礼包】',
          ],
          showCancelButton: false,
          cancelText: '关闭',
          confirmText: '查看礼包',
          action: 'open-gift',
        }, gift, '领取成功');
      } catch (error) {
        await this.handleGiftReceiveError(error, gift);
      } finally {
        this.setGiftReceiving(gift.configId, false);
      }
    },
  },
};
</script>

<style scoped>
.forum_level_page { min-height: 100vh; padding-bottom: 24px; color: #222; background: #f5f6f8; }
.forum_level_state { padding: 72px 20px; color: #999; font-size: 14px; text-align: center; }.forum_level_state p { margin: 0 0 12px; }.forum_level_state button { height: 32px; padding: 0 16px; color: #168cff; background: #fff; border: 1px solid #168cff; border-radius: 16px; }
.forum_level_group_header { position: relative; height: 100px; overflow: hidden; background-position: center; background-size: cover; }.forum_level_group_mask { position: absolute; top: 0; right: 0; bottom: 0; left: 0; background: rgba(0, 0, 0, .28); }.forum_level_group_content { position: absolute; right: 16px; bottom: 2px; left: 16px; display: flex; align-items: center; }
.forum_level_group_icon { width: 60px; height: 60px; flex-shrink: 0; border: 2px solid rgba(255,255,255,.75); border-radius: 8px; object-fit: cover; box-sizing: border-box; }.forum_level_group_info { min-width: 0; flex: 1; margin-left: 12px; }.forum_level_group_info h1 { overflow: hidden; margin: 0; color: #fff; font-size: 16px; line-height: 22px; white-space: nowrap; text-overflow: ellipsis; }.forum_level_group_badge { display: inline-block; height: 18px; margin-top: 3px; padding: 0 7px; color: #fff; font-size: 11px; font-weight: 600; line-height: 18px; background: #168cff; border: 1px solid #ffd83d; border-radius: 9px; box-sizing: border-box; }
.forum_level_user_card { display: flex; align-items: center; margin: 12px 10px 0; padding: 14px 12px; background: #fdf6ee; border: 1px solid #d9bd94; border-radius: 5px; box-sizing: border-box; }
.forum_level_avatar_wrap { position: relative; width: 56px; height: 56px; flex-shrink: 0; }.forum_level_avatar { position: absolute; top: 7px; left: 7px; width: 42px; height: 42px; border-radius: 50%; object-fit: cover; }.forum_level_frame { position: absolute; top: 0; left: 0; width: 56px; height: 56px; object-fit: contain; }
.forum_level_user_info { min-width: 0; flex: 1; margin-left: 10px; }.forum_level_identity { display: flex; align-items: center; font-size: 14px; }.forum_level_identity strong { overflow: hidden; color: #222; font-size: 14px; line-height: 22px; white-space: nowrap; text-overflow: ellipsis; }.forum_level_identity span { margin-left: 8px; padding: 4px 12px; color: #fff; font-size: 14px; font-weight: 600; line-height: 14px; background: #d39c50; border-radius: 4px; }.forum_level_user_info p { margin: 4px 0 3px; color: #999; font-size: 11px; line-height: 16px; }.forum_level_progress { width: 200px; max-width: 100%; border: 1px solid #d9bd94; }
.forum_level_task_section { margin: 8px 10px 0; padding: 10px 10px 8px; background: #fdf6ee; border: 1px solid #d9bd94; border-radius: 5px; box-sizing: border-box; }.forum_level_task_section h3 { display: flex; align-items: center; margin: 0 0 8px; font-size: 15px; line-height: 20px; }.forum_level_task_section h3 > span { width: 3px; height: 14px; margin-right: 6px; background: #ee9f45; border-radius: 2px; }
.forum_level_task { display: flex; align-items: center; min-height: 38px; padding: 6px 10px; background: #fff; border-radius: 4px; font-size: 12px; box-sizing: border-box; }.forum_level_task + .forum_level_task { margin-top: 2px; }.forum_level_task_text { min-width: 0; flex: 1; display: flex; align-items: center; line-height: 18px; }.forum_level_task_text span { overflow: hidden; color: #333; line-height: 18px; white-space: nowrap; text-overflow: ellipsis; }.forum_level_task_text em { margin-left: 6px; color: #aaa; font-style: normal; white-space: nowrap; }.forum_level_rule { margin-top: 8px; padding: 0 10px; color: #666; font-size: 13px; line-height: 1.7; word-break: break-word; }
.forum_gift_item { display: flex; align-items: center; margin: 8px 12px 0; padding: 14px 12px; background: #fff; border-radius: 6px; }.forum_gift_item > img { width: 54px; height: 54px; flex-shrink: 0; border-radius: 6px; object-fit: cover; }.forum_gift_info { min-width: 0; flex: 1; margin: 0 10px; }.forum_gift_info h3 { overflow: hidden; margin: 0; font-size: 15px; white-space: nowrap; text-overflow: ellipsis; }.forum_gift_info p { margin: 6px 0 0; color: #555; font-size: 12px; line-height: 18px; }.forum_gift_info small { display: block; margin-top: 5px; color: #999; font-size: 11px; }
.forum_gift_action { width: 64px; flex-shrink: 0; text-align: center; }.forum_gift_receive_btn { display: inline-flex; width: 64px; align-items: center; justify-content: center; padding: 0; }.forum_gift_receive_btn /deep/ .van-button__content { width: 100%; height: 100%; align-items: center; justify-content: center; }.forum_gift_receive_btn.van-button--disabled { opacity: 1; }.forum_gift_refresh { display: block; margin-top: 6px; color: rgb(255, 18, 0); font-size: 10px; line-height: 14px; text-align: center; white-space: nowrap; }
</style>
